import * as t from 'io-ts';
import { fromEnum, optional } from '../lib/codec';

interface ParamsDictionary {
  [key: string]: string;
}

/**
 * The URL params for delivery instructions endpoints.
 */
export interface ParamsDeliveryInstructions
  extends ParamsDeliveryManagement,
    ParamsDictionary {
  /**
   * The tracking number to be queried.
   */
  trackingNumber: string;
}

/**
 * The list of supported carriers for delivery management endpoints.
 */
export enum ParamDeliveryManagementCarrier {
  UPS = 'ups',
  FEDEX = 'fedex',
}

export const CarrierKindV = fromEnum(
  'ParamDeliveryManagementCarrier',
  ParamDeliveryManagementCarrier,
);

export type CarrierKind = `${ParamDeliveryManagementCarrier}`;

/**
 * The list of supported carriers for scheduled delivery endpoints
 */
export const ParamScheduleDeliveryCarrierV = t.keyof({
  metro: null,
  pilot: null,
  'sagawa-v2': null,
});

export type ParamScheduleDeliveryCarrier = t.TypeOf<
  typeof ParamScheduleDeliveryCarrierV
>;

/**
 * The URL params for delivery management endpoints.
 */
interface ParamsDeliveryManagement extends ParamsDictionary {
  /**
   * The carrier to be queried.
   */
  carrier: ParamDeliveryManagementCarrier;
}

/**
 * Enumerable error codes used by delivery instructions.
 */
export enum ResponseErrorCode {
  /**
   * The carrier queried is not supported.
   */
  UNSUPPORTED_CARRIER = 'UNSUPPORTED_CARRIER',
  /**
   * The carrier returned an invalid response.
   */
  CARRIER_RESPONSE_ERROR = 'CARRIER_RESPONSE_ERROR',
  /**
   * The address submitted could not be verified.
   */
  ADDRESS_VERIFICATION_ERROR = 'ADDRESS_VERIFICATION_ERROR',
  /**
   * The operation is not eligible to be performed.
   */
  INELIGIBLE_ERROR = 'INELIGIBLE_ERROR',
  /**
   * The data required to register the user for an account was insufficient.
   */
  ACCOUNT_REGISTRATION_DATA_INSUFFICIENT_ERROR = 'ACCOUNT_REGISTRATION_DATA_INSUFFICIENT_ERROR',
  /**
   * An operation to register the user for an account failed.
   */
  ACCOUNT_REGISTRATION_ERROR = 'ACCOUNT_REGISTRATION_ERROR',
  /**
   * A request does not have valid input passed.
   */
  REQUEST_VALIDATION_ERROR = 'REQUEST_VALIDATION_ERROR',
  /**
   * The exception error was not handled.
   */
  UNHANDLED_EXCEPTION_ERROR = 'UNHANDLED_EXCEPTION_ERROR',
  /**
   * Request body was expecting an order number that was undefined
   */
  ORDER_NUMBER_UNDEFINED_ERROR = 'ORDER_NUMBER_UNDEFINED_ERROR',
  /**
   * Delivery preferences submission error
   */
  DELIVERY_PREFERENCES_SUBMISSION_ERROR = 'DELIVERY_PREFERENCES_SUBMISSION_ERROR',
  /**
   * Carrier API authentication failed (credentials issue)
   */
  CARRIER_AUTHENTICATION_ERROR = 'CARRIER_AUTHENTICATION_ERROR',
}

const ResponseErrorCodeV = fromEnum('ResponseErrorCode', ResponseErrorCode);

/**
 * Base response interface for signalling whether a query was successful or if
 * there were any errors.
 */
interface Response {
  /**
   * Enumerable error code.
   */
  error?: ResponseErrorCode;
  /**
   * Whether the query was successful.
   */
  success?: boolean;
}

/**
 * Response for whether delivery instructions were submitted successfully.
 */
export const ResponseDeliveryInstructionsDetailsV = t.strict({
  error: optional(ResponseErrorCodeV),
  success: optional(t.boolean),
  hasProfile: optional(t.boolean),
});

export type ResponseDeliveryInstructionsDetails = t.TypeOf<
  typeof ResponseDeliveryInstructionsDetailsV
>;

/**
 * The variants of eligibility a package might have for delivery instructions.
 */
export enum ResponseDeliveryInstructionsEligibilityType {
  /**
   * Ineligible for any type of delivery instructions.
   */
  INELIGIBLE = 'INELIGIBLE',
  /**
   * Eligible to choose and submit an initial leave at option.
   */
  ELIGIBLE_CREATE_LEAVE_AT = 'ELIGIBLE_CREATE_LEAVE_AT',
  /**
   * Eligible to submit an initial security code.
   */
  ELIGIBLE_CREATE_SECURITY_CODE = 'ELIGIBLE_CREATE_SECURITY_CODE',
  /**
   * Eligible to choose and submit an initial leave at option and also an
   * initial security code.
   */
  ELIGIBLE_CREATE_LEAVE_AT_AND_SECURITY_CODE = 'ELIGIBLE_CREATE_LEAVE_AT_AND_SECURITY_CODE',
}

/**
 * A delivery instructions option for the user to choose.
 */
export interface ResponseDeliveryInstructionsLeaveAtOptions {
  /**
   * The display value of the option, translated.
   */
  label: string;
  /**
   * The value of the option.
   */
  value: string;
}

/**
 * Delivery instructions eligibility response.
 */
export const ResponseDeliveryInstructionsEligibilityV = t.strict({
  /**
   * Whether a package is eligible and the type of eligibility it has.
   */
  eligibility: optional(
    fromEnum<ResponseDeliveryInstructionsEligibilityType>(
      'ResponseDeliveryInstructionsEligibilityType',
      ResponseDeliveryInstructionsEligibilityType,
    ),
  ),
  /**
   * The list of delivery instruction options for the user to choose from. No
   * options will be given if the address is not verified or if the package is
   * ineligible.
   */
  leaveAtOptions: optional(
    t.array(
      t.strict({
        label: t.string,
        value: t.string,
      }),
    ),
  ),
  /**
   * The value of delivery instruction if one was previously submitted. This
   * value will not be populated if the address is not verified.
   */
  leaveAtValue: optional(t.string),
  /**
   * Whether a value has been previously submitted.
   */
  leaveAtValuePresent: optional(t.boolean),
  /**
   * Whether the submitted address has been verified.
   */
  addressVerified: optional(t.boolean),
  /**
   * Whether the user may register for an account with the carrier.
   */
  registerAccount: optional(t.boolean),
  /**
   * When set true, show UPS ASR authorization
   */
  asrSignatureRequired: optional(t.boolean),
  /**
   * Set to true when delivery preferences automatically applied
   */
  narvarSubmitted: optional(t.boolean),
});

export interface ResponseDeliveryInstructionsEligibility
  extends Response,
    t.TypeOf<typeof ResponseDeliveryInstructionsEligibilityV> {}

/**
 * Fields used for testing and integrations used by delivery instructions.
 */
interface RequestDeliveryInstructionsTestData<T> {
  /**
   * Data used for testing and integration with carriers.
   */
  testData?: T;
}

/**
 * Fields used for address verification used by delivery instructions.
 */
export interface RequestDeliveryInstructionsAddressVerificationFields {
  /**
   * First name
   */
  firstName: string;
  /**
   * Last name
   */
  lastName: string;
  /**
   * Address line 1
   */
  addressLine1: string;
  /**
   * Address line 2 (optional, can be an empty string)
   */
  addressLine2: string;
  /**
   * City
   */
  city: string;
  /**
   * State code (e.g. CA, NY, TX, etc.)
   */
  stateCode: string;
  /**
   * Postal code
   */
  postalCode: string;
  /**
   * Country code (e.g. US, CA, MX, etc.)
   */
  countryCode: string;
}

/**
 * Shared fields for address verification used by delivery instructions.
 */
export interface RequestDeliveryInstructionsAddressVerification {
  /**
   * Fields used for address verification.
   */
  addressVerification?: RequestDeliveryInstructionsAddressVerificationFields;
  /**
   * Retailer Moniker
   */
  retailerMoniker?: string;
}

/**
 * Request body for checking the eligibility of a package. Validates the users'
 * submitted address to authenticate.
 */
export type RequestDeliveryInstructionsEligibility<T> =
  RequestDeliveryInstructionsTestData<T> &
    RequestDeliveryInstructionsAddressVerification;

/**
 * Request body for submitting delivery instructions and optionally registering
 * for an account with a carrier.
 */
export interface RequestDeliveryInstructionsDetails<T>
  extends RequestDeliveryInstructionsTestData<T>,
    RequestDeliveryInstructionsAddressVerification {
  /**
   * Delivery instructions value for where to leave a package to be sent to the
   * carrier.
   */
  leaveAtValue?: string;
  /**
   * Delivery instructions value for a security code to use to leave a package
   * in a secure area to be sent to the carrier.
   */
  securityCode?: string;
  /**
   * Whether to register this user with the carrier.
   */
  registerAccount?: boolean;
  /**
   * Details required to register this user with the carrier.
   */
  registerAccountDetails?: {
    /**
     * Email for account registration with the carrier.
     */
    email?: string;
    /**
     * Phone number for account registration with the carrier.
     */
    phone?: string;
  };
  allowDriverRelease: boolean;
  /**
   * Email address that was used to save preferences with sublime
   */
  sublimeEmail?: string;
  /**
   * Order Number
   */
  orderNumber?: string;
  /**
   * Set by santorini (https://github.com/narvar/santorini) service auto-applies delivery instructions
   */
  narvarSubmitted?: boolean;
}

/**
 * The URL params for redirect to hold endpoints.
 */
export interface ParamsRedirectToHold
  extends ParamsDeliveryManagement,
    ParamsDictionary {
  /**
   * The tracking number to be queried.
   */
  trackingNumber: string;
}

/**
 * Code/Message from vender/carrier API.
 */
const ReturnCodeMessageV = t.strict(
  {
    code: t.string,
    message: t.string,
    messageCarrier: t.string,
  },
  'ReturnCodeMessageV',
);

export interface ReturnCodeMessage
  extends t.TypeOf<typeof ReturnCodeMessageV> {}

/**
 * Redirect to Hold eligibility response.
 */
export const ResponseRedirectToHoldEligibilityV = t.union(
  [
    t.strict({
      success: t.literal(true),
      warning: t.union([t.null, t.literal('onhold.best.effort')]),
    }),
    t.strict({
      success: t.literal(false),
      error: ResponseErrorCodeV,
    }),
  ],
  'ResponseRedirectToHoldEligibilityV',
);

export type ResponseRedirectToHoldEligibility = t.TypeOf<
  typeof ResponseRedirectToHoldEligibilityV
>;

/**
 * Request body for checking the eligibility of a package for Redirect to Hold. Validates the user's
 * submitted address to authenticate.
 */
export interface RequestRedirectToHoldEligibility {
  /**
   * The unique identifier for the package.  AKA: packageIdentifier.
   * Appears to be required for FedEx RTH API.
   */
  uniqueIdentifier: string;

  /**
   * Carrier associated with the tracking number.  Used in cache key generation.
   */
  carrierMoniker: string;

  /**
   * Retailer associated with the tracking number.  Used in cache key generation.
   */
  retailerMoniker: string;

  /**
   * Fields used for address verification.
   */
  addressVerification: RequestDeliveryInstructionsAddressVerificationFields;
}

/**
 * Redirect to Hold submit request.
 * FedEx requires(?) the location id and address.
 */
export interface RequestRedirectToHoldSubmit {
  /**
   * The unique identifier for the package.  AKA: packageIdentifier.
   * Appears to be required for FedEx RTH API.
   */
  uniqueIdentifier: string;

  locationId: string;
  locationAddress: LocationAddress;

  /**
   * Carrier associated with the tracking number.  Used in cache key generation.
   */
  carrierMoniker: string;

  /**
   * Retailer associated with the tracking number.  Used in cache key generation.
   */
  retailerMoniker: string;

  /**
   * Fields used for address verification.
   */
  addressVerification: RequestDeliveryInstructionsAddressVerificationFields;
}

/**
 * Redirect to Hold submit response.
 */

export const ResponseRedirectToHoldSubmitV = t.union(
  [
    t.strict({
      success: t.literal(true),
      confirmation: t.string,
      messages: t.array(ReturnCodeMessageV),
    }),
    t.strict({
      success: t.literal(false),
      error: ResponseErrorCodeV,
    }),
  ],
  'ResponseRedirectToHoldSubmitV',
);

export type ResponseRedirectToHoldSubmit = t.TypeOf<
  typeof ResponseRedirectToHoldSubmitV
>;

/**
 * Request body for looking up list of locations that supports Redirect to Hold of a package.
 */
export interface RequestRedirectToHoldLocations {
  /**
   * The unique identifier for the package.  AKA: packageIdentifier.
   * Appears to be required for FedEx RTH API.
   */
  uniqueIdentifier: string;

  /**
   * Carrier associated with the tracking number.  Used in cache key generation.
   */
  carrierMoniker: string;

  /**
   * Retailer associated with the tracking number.  Used in cache key generation.
   */
  retailerMoniker: string;

  searchDistance: number;
  searchUnit: 'MI' | 'KM';

  /**
   * Fields used for address verification.
   */
  addressVerification: RequestDeliveryInstructionsAddressVerificationFields;
}

const LocationAddressV = t.intersection(
  [
    t.strict({
      street1: t.string,
      city: t.string,
      stateOrProvince: t.string,
      postalCode: t.string,
      countryCode: t.string,
    }),
    t.partial({
      street2: t.union([t.string, t.null]),
      geo: t.string,
      residential: t.union([t.boolean, t.string]),
    }),
  ],
  'LocationAddressV',
);

const LocationAddressSubmitV = t.intersection(
  [
    t.strict({
      street: t.array(t.string),
      city: t.string,
      stateOrProvince: t.string,
      postalCode: t.string,
      countryCode: t.string,
    }),
    t.partial({
      geo: t.string,
      residential: t.union([t.boolean, t.string]),
    }),
  ],
  'LocationAddressSubmitV',
);

interface LocationAddress extends t.TypeOf<typeof LocationAddressSubmitV> {}

export const DOW_MONDAY = 'Monday';
export const DOW_TUESDAY = 'Tuesday';
export const DOW_WEDNESDAY = 'Wednesday';
export const DOW_THURSDAY = 'Thursday';
export const DOW_FRIDAY = 'Friday';
export const DOW_SATURDAY = 'Saturday';
export const DOW_SUNDAY = 'Sunday';

export const DayOfWeekV = t.union(
  [
    t.literal(DOW_MONDAY),
    t.literal(DOW_TUESDAY),
    t.literal(DOW_WEDNESDAY),
    t.literal(DOW_THURSDAY),
    t.literal(DOW_FRIDAY),
    t.literal(DOW_SATURDAY),
    t.literal(DOW_SUNDAY),
    t.undefined,
  ],
  'DayOfWeekV',
);

const CLOSED = 'CLOSED';
const OPEN = 'OPEN';
const UNKNOWN = 'UNKNOWN';

export const StoreOpenStatusV = t.union(
  [t.literal(OPEN), t.literal(CLOSED), t.literal(UNKNOWN)],
  'StoreOpenStatusV',
);

export type DayOfWeek = t.TypeOf<typeof DayOfWeekV>;

const LocationHoursV = t.intersection(
  [
    t.strict({
      dayOfWeek: DayOfWeekV,
    }),
    t.partial({
      closeAllDay: t.union([t.boolean, t.null]),
      openAllDay: t.union([t.boolean, t.null]),
      hoursRange: t.union([t.string, t.null]),
    }),
  ],
  'LocationHoursV',
);

export interface LocationHours extends t.TypeOf<typeof LocationHoursV> {}

export const GeocodeV = t.partial(
  {
    lat: t.union([t.number, t.null]),
    lng: t.union([t.number, t.null]),
  },
  'GeocodeV',
);

export interface Geocode extends t.TypeOf<typeof GeocodeV> {}

export const LocationDetailV = t.intersection(
  [
    t.strict({
      id: t.string,
      name: t.union([t.string, t.null]),
      providerId: t.string,
      address: LocationAddressV,
      hours: t.union([t.array(LocationHoursV), t.null]),
    }),
    t.partial({
      additionalDescription: t.union([t.string, t.null]),
      email: t.union([t.string, t.null]),
      phone: t.union([t.string, t.null]),
      geocode: t.union([GeocodeV, t.null]),
      directionUrl: t.union([t.string, t.null]),
      distance: t.union([t.number, t.string]),
      distanceUnit: t.string,
      openStatus: t.union([StoreOpenStatusV, t.null]),
      openStatusTime: t.union([t.string, t.null]),
    }),
  ],
  'LocationDetailV',
);

export interface LocationDetail extends t.TypeOf<typeof LocationDetailV> {}

/**
 * Redirect to Hold locations response.
 */
export const ResponseRedirectToHoldLocationsV = t.union(
  [
    t.intersection([
      t.strict({
        success: t.literal(true),
        locations: t.array(LocationDetailV),
      }),
      t.partial({
        messages: t.array(ReturnCodeMessageV),
      }),
    ]),
    t.strict({
      success: t.literal(false),
      error: ResponseErrorCodeV,
    }),
  ],
  'ResponseRedirectToHoldLocationsV',
);

export type ResponseRedirectToHoldLocations = t.TypeOf<
  typeof ResponseRedirectToHoldLocationsV
>;

/**
 * Request for Redirect-to-Hold Status
 * Checks Cache for RTH settings
 */
export interface RequestRedirectToHoldStatus<T>
  extends RequestDeliveryInstructionsTestData<T> {
  /**
   * Carrier associated with the tracking number.  Used in cache key generation.
   */
  carrierMoniker: string;

  /**
   * Retailer associated with the tracking number.  Used in cache key generation.
   */
  retailerMoniker: string;
}

const RedirectToHoldStatusV = t.partial(
  {
    eligible: t.union([t.boolean, t.null]),
    hasNearbyLocations: t.union([t.boolean, t.null]),
    scheduled: t.union([t.boolean, t.null]),
    packageIdentifier: t.union([t.string, t.null]),
    redirectRequestNumber: t.union([t.string, t.null]),
    confirmationNumber: t.union([t.string, t.null]),
    storeLocation: t.union([LocationDetailV, t.null]),
  },
  'RedirectToHoldStatusV',
);

export interface RedirectToHoldStatus
  extends t.TypeOf<typeof RedirectToHoldStatusV> {}

/**
 * Redirect to Hold Status response.
 */
export const ResponseRedirectToHoldStatusV = t.union(
  [
    t.strict({
      success: t.literal(true),
      redirectToHold: t.union([RedirectToHoldStatusV, t.null]),
    }),
    t.intersection([
      t.strict({
        success: t.literal(false),
        error: ResponseErrorCodeV,
      }),
      t.partial({
        redirectToHold: t.union([RedirectToHoldStatusV, t.null]),
      }),
    ]),
  ],
  'ResponseRedirectToHoldEligibilityV',
);

export type ResponseRedirectToHoldStatus = t.TypeOf<
  typeof ResponseRedirectToHoldStatusV
>;
